import { useTranslation } from "react-i18next";

import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { z } from "zod";

const filterC3PortfoliosSchema = z.object({
  client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  reporting_currency_contains: z.string().optional(),
  bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  bank_reporting_currency_contains: z.string().optional(),
  account_type_contains: z.string().optional(),
  investment_type_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  contract_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  collateral_contains: z.string().optional(),
});

export type FilterC3PortfoliosSchema = z.infer<typeof filterC3PortfoliosSchema>;
const filterC3PortfoliosFields = filterC3PortfoliosSchema.keyof().enum;

const portfolioDepFields: readonly DepField<FilterC3PortfoliosSchema>[] = [
  {
    name: filterC3PortfoliosFields.bank_name_contains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC3PortfoliosFields.client_contains,
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC3PortfoliosFields.vehicle_contains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC3PortfoliosFields.investment_type_contains,
    key: "investment_type",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC3PortfoliosFields.contract_name_contains,
    key: "contract_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC3PortfoliosFields.name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterC3PortfoliosFields.code_contains,
    key: "code",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC3PortfoliosFields.account_name_contains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

const TABLE_NAME = Tables.C3_PORTFOLIOS;
const client = Clients.C3;

export function FilterC3Portfolios() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: filterC3PortfoliosFields.client_contains,
      label: t(`${TABLE_NAME}.searchbox.client_contains`, { defaultValue: "Client" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "client",
      filters: makeFiltersBuilder<FilterC3PortfoliosSchema>(
        portfolioDepFields,
        filterC3PortfoliosFields.client_contains,
      ),
    },
    {
      name: filterC3PortfoliosFields.vehicle_contains,
      label: t(`${TABLE_NAME}.searchbox.vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterC3PortfoliosSchema>(
        portfolioDepFields,
        filterC3PortfoliosFields.vehicle_contains,
      ),
    },
    {
      name: filterC3PortfoliosFields.account_name_contains,
      label: t(`${TABLE_NAME}.searchbox.account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterC3PortfoliosSchema>(
        portfolioDepFields,
        filterC3PortfoliosFields.account_name_contains,
      ),
    },
    {
      name: filterC3PortfoliosFields.name_contains,
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterC3PortfoliosSchema>(
        portfolioDepFields,
        filterC3PortfoliosFields.name_contains,
      ),
    },
    {
      name: filterC3PortfoliosFields.currency_contains,
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Portfolio currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: filterC3PortfoliosFields.reporting_currency_contains,
      label: t(`${TABLE_NAME}.searchbox.reporting_currency_contains`, { defaultValue: "Reporting currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: filterC3PortfoliosFields.bank_name_contains,
      label: t(`${TABLE_NAME}.searchbox.bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterC3PortfoliosSchema>(
        portfolioDepFields,
        filterC3PortfoliosFields.bank_name_contains,
      ),
    },
    {
      name: filterC3PortfoliosFields.account_type_contains,
      label: t(`${TABLE_NAME}.searchbox.account_type_contains`, { defaultValue: "Account type" }),
      type: "select",
      useTranslation: true,
      client: Clients.C3,
      table_name: Tables.C3_PORTFOLIOS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "account_type",
    },
    {
      name: filterC3PortfoliosFields.bank_reporting_currency_contains,
      label: t(`${TABLE_NAME}.searchbox.bank_reporting_currency_contains`, { defaultValue: "Management currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: filterC3PortfoliosFields.investment_type_contains,
      label: t(`${TABLE_NAME}.searchbox.investment_type_contains`, { defaultValue: "Investment type" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "investment_type",
      filters: makeFiltersBuilder<FilterC3PortfoliosSchema>(
        portfolioDepFields,
        filterC3PortfoliosFields.investment_type_contains,
      ),
    },
    {
      name: filterC3PortfoliosFields.contract_name_contains,
      label: t(`${TABLE_NAME}.searchbox.contract_name_contains`, { defaultValue: "Contract name" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "contract_name",
      filters: makeFiltersBuilder<FilterC3PortfoliosSchema>(
        portfolioDepFields,
        filterC3PortfoliosFields.contract_name_contains,
      ),
    },
    {
      name: filterC3PortfoliosFields.code_contains,
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "code",
      filters: makeFiltersBuilder<FilterC3PortfoliosSchema>(
        portfolioDepFields,
        filterC3PortfoliosFields.code_contains,
      ),
    },
    {
      name: filterC3PortfoliosFields.collateral_contains,
      label: t(`${TABLE_NAME}.searchbox.collateral_contains`, { defaultValue: "Pledged securities account" }),
      type: "select",
      useTranslation: false,
      options: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
    },
  ];

  return (
    <FilterTable
      client={client}
      schema={filterC3PortfoliosSchema}
      tableName={Tables.C3_PORTFOLIOS}
      fields={fields}
      columns={2}
    />
  );
}
